import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { RoleBasedRoute } from "./components/RoleBasedRoute";
import { AppLayoutSkeleton, PageSkeleton } from "./components/PageSkeleton";

// Lazy load all pages and layout for code splitting
const AppLayout = lazy(() =>
  import("./components/AppLayout").then((m) => ({ default: m.AppLayout })),
);
const Login = lazy(() => import("./pages/login/Login"));
const Register = lazy(() => import("./pages/register/Register"));
const VerifyEmail = lazy(() => import("./pages/verify-email/VerifyEmail"));
const ForgotPassword = lazy(
  () => import("./pages/forgot-password/ForgotPassword"),
);
const ResetPassword = lazy(() => import("./pages/reset-password/ResetPassword"));
const Home = lazy(() => import("./pages/home/Home"));
const Profile = lazy(() => import("./pages/profile/Profile"));
const Settings = lazy(() => import("./pages/settings/Settings"));
// const Counters = lazy(() => import("./pages/counters/Counters"));
const Users = lazy(() => import("./pages/users/Users"));
const Invitation = lazy(() => import("./pages/invitation/Invitation"));
const ActivityHistory = lazy(
  () => import("./pages/activity-history/ActivityHistory"),
);
const BusLines = lazy(() => import("./pages/bus-lines/BusLines"));
const Stations = lazy(() => import("./pages/stations/Stations"));
const Directions = lazy(() => import("./pages/directions/Directions"));
const RoutesPage = lazy(() => import("./pages/routes/Routes"));
const SimpleTimetables = lazy(
  () => import("./pages/simple-timetables/SimpleTimetables"),
);
const Timetables = lazy(() => import("./pages/timetables/Timetables"));
const UrbanPricing = lazy(() => import("./pages/pricing/UrbanPricing"));
const SuburbanZonePricing = lazy(
  () => import("./pages/pricing/SuburbanZonePricing"),
);
const IntercityPricing = lazy(() => import("./pages/pricing/IntercityPricing"));
const IntercityPriceListDetail = lazy(
  () => import("./pages/pricing/IntercityPriceListDetail"),
);
const Announcements = lazy(() => import("./pages/announcements/Announcements"));
const LostFound = lazy(() => import("./pages/lost-found/LostFound"));
const LostFoundDetail = lazy(
  () => import("./pages/lost-found/LostFoundDetail"),
);
const AiLogs = lazy(() => import("./pages/ai-logs/AiLogs"));
const AiLogDetail = lazy(() => import("./pages/ai-logs/AiLogDetail"));
const SuburbanRoutes = lazy(
  () => import("./pages/suburban-routes/SuburbanRoutes"),
);

export function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Suspense fallback={<AppLayoutSkeleton />}>
              <AppLayout sideBarCollapsible={true} />
            </Suspense>
          </ProtectedRoute>
        }
      >
        <Route
          index
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Home />
            </Suspense>
          }
        />
        <Route
          path="profile"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Profile />
            </Suspense>
          }
        />
        <Route
          path="settings"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Settings />
            </Suspense>
          }
        />
        {/* <Route
          path="counters"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Counters />
            </Suspense>
          }
        /> */}
        <Route
          path="users"
          element={
            <RoleBasedRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
              <Suspense fallback={<PageSkeleton />}>
                <Users />
              </Suspense>
            </RoleBasedRoute>
          }
        />
        <Route
          path="activity-history"
          element={
            <RoleBasedRoute allowedRoles={["SUPERADMIN"]}>
              <Suspense fallback={<PageSkeleton />}>
                <ActivityHistory />
              </Suspense>
            </RoleBasedRoute>
          }
        />
        <Route
          path="bus-lines"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <BusLines />
            </Suspense>
          }
        />
        <Route
          path="stations"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Stations />
            </Suspense>
          }
        />
        <Route
          path="directions"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Directions />
            </Suspense>
          }
        />
        <Route
          path="routes"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <RoutesPage />
            </Suspense>
          }
        />
        <Route
          path="simple-timetables"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <SimpleTimetables />
            </Suspense>
          }
        />
        <Route
          path="timetables"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Timetables />
            </Suspense>
          }
        />
        <Route
          path="urban-pricing"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <UrbanPricing />
            </Suspense>
          }
        />
        <Route
          path="suburban-pricing"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <SuburbanZonePricing />
            </Suspense>
          }
        />
        <Route
          path="intercity-pricing"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <IntercityPricing />
            </Suspense>
          }
        />
        <Route
          path="intercity-pricing/:id"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <IntercityPriceListDetail />
            </Suspense>
          }
        />
        <Route
          path="announcements"
          element={
            <RoleBasedRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
              <Suspense fallback={<PageSkeleton />}>
                <Announcements />
              </Suspense>
            </RoleBasedRoute>
          }
        />
        <Route
          path="lost-found"
          element={
            <RoleBasedRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
              <Suspense fallback={<PageSkeleton />}>
                <LostFound />
              </Suspense>
            </RoleBasedRoute>
          }
        />
        <Route
          path="lost-found/:id"
          element={
            <RoleBasedRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
              <Suspense fallback={<PageSkeleton />}>
                <LostFoundDetail />
              </Suspense>
            </RoleBasedRoute>
          }
        />
        <Route
          path="ai-logs"
          element={
            <RoleBasedRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
              <Suspense fallback={<PageSkeleton />}>
                <AiLogs />
              </Suspense>
            </RoleBasedRoute>
          }
        />
        <Route
          path="ai-logs/:id"
          element={
            <RoleBasedRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
              <Suspense fallback={<PageSkeleton />}>
                <AiLogDetail />
              </Suspense>
            </RoleBasedRoute>
          }
        />
        <Route
          path="suburban-routes"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <SuburbanRoutes />
            </Suspense>
          }
        />
      </Route>
      <Route
        path="/login"
        element={
          <Suspense fallback={<PageSkeleton minimal />}>
            <Login />
          </Suspense>
        }
      />
      <Route
        path="/register"
        element={
          <Suspense fallback={<PageSkeleton minimal />}>
            <Register />
          </Suspense>
        }
      />
      <Route
        path="/verify-email/:token"
        element={
          <Suspense fallback={<PageSkeleton minimal />}>
            <VerifyEmail />
          </Suspense>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <Suspense fallback={<PageSkeleton minimal />}>
            <ForgotPassword />
          </Suspense>
        }
      />
      <Route
        path="/reset-password/:token"
        element={
          <Suspense fallback={<PageSkeleton minimal />}>
            <ResetPassword />
          </Suspense>
        }
      />
      <Route
        path="/token/:token"
        element={
          <Suspense fallback={<PageSkeleton minimal />}>
            <Invitation />
          </Suspense>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
